import { useMemo } from 'react';
import { useProjectSettingsSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources';
import { findItemAndStack } from '@atlassian/jira-navigation-apps-sidebar-nav4-connect/src/common/utils/find-item-stack';
import { transformLinkV4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-connect/src/common/utils/transform-link';
import { useRouter } from '@atlassian/jira-router';
import { LEGACY_CONNECT_ITEM_IDS } from './constants';
import { useProjectSettingsForgeApps } from './utils';

export const useConnectApps = () => {
	const {
		data: connectResourceItems,
		error,
		loading,
	} = useProjectSettingsSidebarConnectAddonsNavigationResource();

	const connectApps = useMemo(() => {
		if (connectResourceItems == null) {
			return [];
		}

		const transformedConnectApps = [];

		for (const item of connectResourceItems) {
			const transformedItem = transformLinkV4(item);

			if (transformedItem && !LEGACY_CONNECT_ITEM_IDS.has(transformedItem.id)) {
				transformedConnectApps.push(transformedItem);
			}
		}

		return transformedConnectApps;
	}, [connectResourceItems]);

	return {
		data: connectApps,
		loading,
		error,
	};
};

export const useAppItems = (baseSettingsUrl: string) => {
	const {
		data: connectApps,
		error: connectAppsError,
		loading: connectAppsLoading,
	} = useConnectApps();

	const {
		data: forgeApps,
		error: forgeAppsError,
		loading: forgeAppsLoading,
	} = useProjectSettingsForgeApps(baseSettingsUrl);

	const sortedItems = useMemo(
		() =>
			[
				...forgeApps.map((item) => ({ type: 'forge' as const, item })),
				...connectApps.map((item) => ({ type: 'connect' as const, item })),
			].sort((a, b) => a.item.name.localeCompare(b.item.name)),
		[forgeApps, connectApps],
	);

	return {
		data: sortedItems,
		loading: connectAppsLoading || forgeAppsLoading,
		error: connectAppsError || forgeAppsError,
	};
};

export const useIsConnectAppActive = () => {
	const [{ route, location }] = useRouter();
	const { data: connectApps, error, loading } = useConnectApps();

	const isActive = useMemo(
		() => Boolean(findItemAndStack(connectApps, route.name, location)?.selected),
		[connectApps, location, route.name],
	);

	return { isActive, loading, error };
};

export const useIsForgeAppActive = (baseSettingsUrl: string) => {
	const [{ location }] = useRouter();
	const { data: forgeApps, error, loading } = useProjectSettingsForgeApps(baseSettingsUrl);

	const isActive = useMemo(
		() => forgeApps.some((item) => item.url === location.pathname),
		[forgeApps, location],
	);

	return { isActive, loading, error };
};

export const useIsAppItemActive = (baseSettingsUrl: string) => {
	const {
		isActive: isConnectAppActive,
		loading: connectAppsLoading,
		error: connectAppsError,
	} = useIsConnectAppActive();
	const {
		isActive: isForgeAppActive,
		loading: forgeAppsLoading,
		error: forgeAppsError,
	} = useIsForgeAppActive(baseSettingsUrl);

	return {
		isActive: isConnectAppActive || isForgeAppActive,
		loading: connectAppsLoading || forgeAppsLoading,
		error: connectAppsError || forgeAppsError,
	};
};
